import React, {Component} from "react";
import {Link} from "react-router-dom";
import PropTypes from "prop-types";
import Button from "react-toolbox/lib/button/Button";
import SegmentedProgress from "./SegmentedProgress";
import ValidatedForm, {FormValidator, UtilFieldValidators, UtilDataValidators} from "./form/ValidatedForm";
import { InputAdapter } from "./form/FormAdapters.js";
import { Errors } from "../Strings.js";
import "./Signup.css";

class Signup extends Component {

	constructor(props) {
		super(props);

		this.nextStep = this.nextStep.bind(this);
		this.findAccount = this.findAccount.bind(this);
		this.verifyAccount = this.verifyAccount.bind(this);
		this.createAccount = this.createAccount.bind(this);

		this.userID = null;

		this.state = {
			signupStep: SignupStep.intro,
			challenge: {},
			processStatus: CreateStatus.inProgress,
			lastStepStatus: CreateStatus.succeeded,
			lastStepResultMessage: null,
			lastStepFieldValidationMessages: null
		};
	}
	
	nextStep(updatedState) {
		let additionalState = updatedState ? updatedState : {};
		let newState = Object.assign({},
			additionalState,
			{
				signupStep: SignupStep.nextAfter(this.state.signupStep)
			}
		);
		this.setState(newState);
	}

	findAccount(accountNumber, zipCode) {
		let requestData = JSON.stringify({
			"companyRef": accountNumber,
			"postalCode": zipCode
		});
		let request = {
			method: "post",
			url: "/signup/acctChallenge",
			data: requestData
		};

		this.props.server(request).then(response => {
			let challenge = response.data;
			//If the info sent doesn't match, the server does not
			//return an error, it just returns null for the question.
			if (challenge.question) {
				this.nextStep({
					challenge: challenge, 
					lastStepStatus: CreateStatus.succeeded, 
					lastStepResultMessage: null
				});
			} else {
				let msg = "The Account Number and Zip Code you entered do not match an account in our records.";
				this.setState({
					lastStepStatus: CreateStatus.failed, 
					lastStepResultMessage: msg
				});
			}
		}).catch(error => {
			if (error.response) {
				//Server returned an error code
				let msg = "The server encountered an error processing the request: "+
					error.response.status+": "+error.message;
				this.setState({lastStepStatus: CreateStatus.failed, lastStepResultMessage: msg});
			} else if (error.request) {
				//The request was made, but no response was received.
				let msg = "The server is not responding. Please try again later.";
				this.setState({lastStepStatus: CreateStatus.failed, lastStepResultMessage: msg});
			} else {
				//A problem was found in the request before it was sent.
				let msg = "Programming error: "+error.message;
				this.setState({lastStepStatus: CreateStatus.failed, lastStepResultMessage: msg});
			}
		});

	}

	verifyAccount(challengeAnswer) {
		let combinedData = Object.assign({}, 
			this.state.challenge,
			challengeAnswer
		);
		delete combinedData.question;
		delete combinedData.companyName;
		delete combinedData.contactId;
		let requestData = JSON.stringify(combinedData);
		let request = {
			method: "post",
			url: "signup/verifyAcct",
			data: requestData
		};
		this.props.server(request).then((response) => {
			let challenge = response.data;
			this.nextStep({
				challenge: challenge, 
				lastStepStatus: CreateStatus.succeeded
			});
		}).catch(error => {
			if (error.response) {
				//Server returned an error code
				let msg = "Your response did not match our records";
				this.setState({lastStepStatus: CreateStatus.failed, lastStepResultMessage: msg});
			} else if (error.request) {
				//The request was made, but no response was received.
				let msg = "The server is not responding. Please try again later.";
				this.setState({lastStepStatus: CreateStatus.failed, lastStepResultMessage: msg});
			} else {
				//A problem was found in the request before it was sent.
				let msg = "Programming error: "+error.message;
				this.setState({lastStepStatus: CreateStatus.failed, lastStepResultMessage: msg});
			}
		});
	}

	createAccount(newUserInfo) {
		let combinedData = Object.assign({}, 
			this.state.challenge,
			newUserInfo
		);
		delete combinedData.companyName;
		this.userID = combinedData.loginName;

		let requestData = JSON.stringify(combinedData);
		let request = {
			method: "post",
			url: "signup/createAcct",
			data: requestData
		};

		this.props.server(request).then(() => {
			let challenge = {};
			this.props.setUserIDAction(this.userID);
			this.nextStep({
				challenge: challenge, 
				lastStepStatus: CreateStatus.succeeded
			});
		}).catch(error => {
			let userMessages = Errors.messagesForServerError(error);
			var stateData = {
				lastStepStatus: CreateStatus.failed
			};
			if (userMessages.requestError) {
				let msg = "An Account could not be created: "+userMessages.requestError;
				stateData.lastStepResultMessage = msg;
			} else {
				stateData.lastStepResultMessage = null;
			}
			stateData.lastStepFieldValidationMessages = userMessages.fieldErrors;
			this.setState(stateData);
		});
	}

	getSignupUI() {
		let segmentTitles = ["Account Number & Zip Code", "Transaction Amount", "Create a User ID & Password"];
		let currentUI;
		switch(this.state.signupStep) {
		case SignupStep.intro:
			currentUI = 
				<SignupIntro 
					appSettings={this.props.appSettings} 
					nextAction={this.nextStep}
				/>;
			break;
		case SignupStep.accountNo:
			currentUI = 
				<React.Fragment>
					<SegmentedProgress 
						segmentTitles={segmentTitles}
						currentSegment={this.state.signupStep}
					/>
					<SignupAccountZip
						lastStepStatus={this.state.lastStepStatus}
						lastStepResultMessage={this.state.lastStepResultMessage}
						findAccountAction={this.findAccount}
					/>
				</React.Fragment>;
			break;
		case SignupStep.challenge:
			currentUI = 
				<React.Fragment>
					<SegmentedProgress 
						segmentTitles={segmentTitles}
						currentSegment={this.state.signupStep}
					/>
					<SignupChallenge
						challengeData={this.state.challenge}
						lastStepStatus={this.state.lastStepStatus}
						lastStepResultMessage={this.state.lastStepResultMessage}
						verifyAccountAction={this.verifyAccount}
					/>
				</React.Fragment>;
			break;
		case SignupStep.createLogin:
			currentUI = 
				<React.Fragment>
					<SegmentedProgress 
						segmentTitles={segmentTitles}
						currentSegment={this.state.signupStep}
					/>
					<CreateLogin 
						companyInfo={this.state.challenge}
						lastStepStatus={this.state.lastStepStatus}
						lastStepResultMessage={this.state.lastStepResultMessage}
						lastStepFieldValidationMessages={this.state.lastStepFieldValidationMessages}
						createAccountAction={this.createAccount}
					/>
				</React.Fragment>;
			break;
		case SignupStep.success:
			currentUI = 
				<SignupSuccess
					history={this.props.history}
				/>;
			break;
		default: 
			currentUI = <h1>Programming Error: Unknown Signup Step {this.state.signupStep}</h1>;
		}
		return currentUI;
	}

	render() {
		return (
			<div className="signupPage">
				<h2>Create A New Account</h2>
				{this.getSignupUI()}
			</div>
		);
	}
}

Signup.propTypes = {
	appSettings: PropTypes.object,
	match: PropTypes.object,
	history: PropTypes.object,
	server: PropTypes.func,
	setUserIDAction: PropTypes.func
};

const CreateStatus = {
	inProgress: 1,
	succeeded: 10,
	failed: 20
};

const SignupStep = {
	intro: 0,
	accountNo: 1,
	challenge: 2,
	createLogin: 3,
	success: 4,

	nextAfter: function(nextAfter) {
		switch (nextAfter) {
		case SignupStep.intro:
			return SignupStep.accountNo;
		case SignupStep.accountNo:
			return SignupStep.challenge;
		case SignupStep.challenge:
			return SignupStep.createLogin;
		case SignupStep.createLogin:
			return SignupStep.success;
		default:
			return SignupStep.intro;
		}
	}
};

function SignupIntro(props) {
	return (
		<div className="createAccountForm">
			<div className="signupStepExplanation">
				<p>To create an account, you will be asked to provide:</p>
				<ol>
					<li>
						<strong>Your account number: </strong>
						Your account number can be found on any statement,
						invoice, or receipt that you have received from us.
					</li>
					<li>
						<strong>Your zip code: </strong>
						This should be the zip code for the address where we send 
						your statements, invoices and receipts.
					</li>
					<li>
						<strong>A transaction amount: </strong>
						For security, you will be asked to provide an amount from 
						a recent statement, invoice, receipt or ad.
					</li>
				</ol>
				<p>
					If you do not have all the information you need to sign up for a new account,
					feel free to <Link to="/contact">contact us</Link>.
				</p>
				<Button raised primary label="Begin" className="signupNextButton" onClick={props.nextAction}/>
			</div>
		</div>
	);
}

SignupIntro.propTypes = {
	appSettings: PropTypes.object,
	nextAction: PropTypes.func
};

class SignupAccountZip extends Component {
	constructor(props) {
		super(props);

		this.lookupAccount = this.lookupAccount.bind(this);

		this.formValidator = new FormValidator(
			{
				accountNumber: UtilFieldValidators.valueRequired,
				zipCode: UtilFieldValidators.valueRequired
			}, 
			UtilDataValidators.allFieldsRequired
		);

		this.state = {
			accountNumber: "",
			zipCode: ""
		};
	}

	lookupAccount(formData) {
		this.props.findAccountAction(formData.accountNumber, formData.zipCode);
		// this.props.findAccountAction(this.state.accountNumber, this.state.zipCode);
	}

	render() {
		let errMsg = this.props.lastStepStatus === CreateStatus.failed ?
			this.props.lastStepResultMessage :
			null;
		let validationMsgs = {};
		return (
			<div className="createAccountForm">
				<h3>Step 1: Your Account Number and Zip Code</h3>
				<ValidatedForm
					onSubmit={this.lookupAccount} 
					dataValidationMessage = {errMsg}
					validationMessages={validationMsgs}
					formValidator={this.formValidator}
					submitButtonLabel="Lookup Account"
				>
					<InputAdapter 						
						fieldName="accountNumber" 
						componentProps= {{
							type: "text",
							label: "Account Number",
							required: true
						}}
					/>
					<p>
						You can find your account number on any statement, invoice,
						or receipt that you have received from us.
					</p>
					<InputAdapter 
						fieldName="zipCode" 
						componentProps= {{
							type: "text",
							label: "Zip Code",
							required: true
						}}
					/>
					<p>
						This should be the zip code for the address where we send 
						your statements, invoices and receipts.
					</p>
				</ValidatedForm>
			</div>
		);
	}
}

SignupAccountZip.propTypes = {
	lastStepStatus: PropTypes.number,
	lastStepResultMessage: PropTypes.string,
	findAccountAction: PropTypes.func
};

export class SignupChallenge  extends Component {

	constructor(props) {
		super(props);

		this.formValidator = new FormValidator(
			{
				challengeAnswer: UtilFieldValidators.valueRequired
			}, 
			UtilDataValidators.allFieldsRequired
		);

		this.verifyAccount = this.verifyAccount.bind(this);
	}	
	
	verifyAccount(formData) {
		this.props.verifyAccountAction(formData);
	}

	render() {
		let errMsg = this.props.lastStepStatus === CreateStatus.failed ?
			this.props.lastStepResultMessage : null;
		return (
			<div className="createAccountForm">
				<h3>Step 2: Transaction Amount</h3>
				<div className="signupStepExplanation">
					<p>
						Your account has been located, and now, for security reasons,
						we need to ask you to verify some information regarding a
						specific transaction amount.
					</p>
				</div>
				<ValidatedForm
					onSubmit={this.verifyAccount}
					dataValidationMessage={errMsg}
					validationMessages={this.props.lastStepFieldValidationMessages}
					formValidator={this.formValidator}
					submitButtonLabel="Verify Account"
				>
					<h3>Transaction Amount Question</h3>
					<p>{this.props.challengeData.question}</p>
					<InputAdapter
						fieldName="challengeAnswer"
						componentProps={{
							type: "number", 
							step: "0.01",
							label: "Amount",
							required: true
						}}
					/>
				</ValidatedForm>
			</div>
		);
	}
}

SignupChallenge.propTypes = {
	challengeData: PropTypes.object,
	lastStepStatus: PropTypes.number,
	lastStepResultMessage: PropTypes.string,
	lastStepFieldValidationMessages: PropTypes.object,
	verifyAccountAction: PropTypes.func
};

export const ConfirmPasswordValidator = (value, formData) => {
	var msg;
	if (value !== null && value !== "" && value !== formData.password) {
		msg = "Passwords do not match" ;
	} else {
		msg = null;
	}
	return msg;
};

class CreateLogin extends Component {
	constructor(props) {
		super(props);

		this.createAccount=this.createAccount.bind(this);

		this.formValidator = new FormValidator({confirmedPassword: ConfirmPasswordValidator}, UtilDataValidators.allFieldsRequired);

	}

	createAccount(formData) {
		delete formData.confirmedPassword;
		this.props.createAccountAction(formData);
	}
	
	render() {
		let errMsg = this.props.lastStepStatus === CreateStatus.failed ?
			this.props.lastStepResultMessage :
			null;
		return (
			<div className="createAccountForm">
				<h3>Welcome {this.props.companyInfo.companyName}</h3>
				<p>
					Validation of your account was successful. Now you can create
					a User ID and Password to access your account.
				</p>
				<h3>Step 3: Create a User ID and Password</h3>
				<ValidatedForm
					onSubmit={this.createAccount}
					dataValidationMessage={errMsg}
					validationMessages={this.props.lastStepFieldValidationMessages}
					formValidator={this.formValidator}
					submitButtonLabel="Create Account"
				>
					<InputAdapter
						fieldName="firstName"
						componentProps = {{
							type: "text",
							label:"Your First Name",
							required: true
						}}
					/>
					<InputAdapter
						fieldName="lastName"
						componentProps = {{
							type: "text",
							label:"Your Last Name",
							required: true
						}}
					/>
					<InputAdapter
						fieldName="emailAddr"
						componentProps={{
							type: "email",
							label:"Your Email Address",
							required: true
						}}
					/>
					<InputAdapter
						fieldName="loginName"
						componentProps={{
							type: "text",
							label: "Choose a User ID",
							required: true
						}}
					/>
					<InputAdapter
						fieldName="password"
						componentProps={{
							type: "password",
							label: "Choose a password",
							required: true
						}}
					/>
					<InputAdapter
						fieldName="confirmedPassword"
						componentProps={{
							type: "password",
							label: "Confirm Password",
							required: true
						}}
					/>
				</ValidatedForm>
			</div>

		);
	}
}

CreateLogin.propTypes = {
	companyInfo: PropTypes.object,
	lastStepStatus: PropTypes.number,
	lastStepResultMessage: PropTypes.string,
	lastStepFieldValidationMessages: PropTypes.object,
	createAccountAction: PropTypes.func
};

function SignupSuccess(props) {
	return (
		<div className="createAccountForm">
			<h2>Account Successfully Created</h2>
			<p>You may now sign in to your account.</p>
			<Button 
				raised primary 
				label="Sign In" 
				className="signupNextButton" 
				onClick={() => {props.history.replace("/");}}
			/>	
		</div>
	);
}

SignupSuccess.propTypes = {
	history: PropTypes.object
};

// class NewUserInfo {
// 	constructor() {
// 		this.firstName = "";
// 		this.lastName = "";
// 		this.loginName = "";
// 		this.password = "";
// 		this.emailAddr = "";
// 	}

// 	isValid() {
// 		return (
// 			this.firstName && this.firstName !== "" &&
// 			this.lastName && this.lastName !== "" &&
// 			this.loginName && this.loginName !== "" &&
// 			this.passWord && this.passWord !== "" &&
// 			this.emailAddr && this.emailAddr !== ""
// 		);
// 	}
// }

export default Signup;


// WEBPACK FOOTER //
// ./src/components/Signup.js